// Preload critical Font Awesome webfonts so icons (e.g. the bottom-toolbar `far` icons
// and the chat Note `fa-sharp fa-regular` pen) render without a visible delay on the
// live env / CDN, where the woff2 download latency is non-trivial. On localhost the
// files come back instantly so the issue is invisible there, but on production the
// @font-face rule (with `font-display: block`) holds the glyph invisible until the
// woff2 file finishes downloading, which only starts AFTER the component using that
// family is rendered.
//
// Importing the woff2 files here gives them stable webpack-emitted URLs that match
// the URLs referenced from the Font Awesome CSS, and the <link rel="preload"> hint
// + explicit FontFace.load() kicks off the download as soon as the app boots
// (i.e. before BottomToolbar.vue mounts).

import faRegularUrl from '@/assets/fontAwesome/kit-5e5db45630/webfonts/fa-regular-400.woff2';
import faSolidUrl from '@/assets/fontAwesome/kit-5e5db45630/webfonts/fa-solid-900.woff2';
import faSharpRegularUrl from '@/assets/fontAwesome/kit-5e5db45630/webfonts/fa-sharp-regular-400.woff2';

const FONTS_TO_PRELOAD = [
  { url: faRegularUrl, family: 'Font Awesome 6 Pro', weight: '400', style: 'normal' },
  { url: faSolidUrl, family: 'Font Awesome 6 Pro', weight: '900', style: 'normal' },
  // Sharp regular is a separate family. Icons such as the chat Note pen
  // (`fa-sharp fa-regular`) stay invisible until this file arrives.
  { url: faSharpRegularUrl, family: 'Font Awesome 6 Sharp', weight: '400', style: 'normal' },
];

function addPreloadHint(url) {
  if (!url || typeof document === 'undefined') return;
  if (document.head.querySelector(`link[rel="preload"][href="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'font';
  link.type = 'font/woff2';
  link.crossOrigin = 'anonymous';
  link.href = url;
  document.head.appendChild(link);
}

function forceLoad({ url, family, weight, style }) {
  if (!url || typeof document === 'undefined' || typeof FontFace === 'undefined') return;
  try {
    const face = new FontFace(family, `url(${url}) format('woff2')`, {
      weight,
      style,
      display: 'block',
    });
    face
      .load()
      .then((loaded) => {
        document.fonts.add(loaded);
      })
      .catch(() => {
        // Silently ignore; CSS @font-face will still load the font on demand.
      });
  } catch (e) {
    // Older browsers without FontFace constructor; preload <link> hint above is enough.
  }
}

FONTS_TO_PRELOAD.forEach((font) => {
  addPreloadHint(font.url);
  forceLoad(font);
});
